import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useMerchant } from './MerchantContext'
import useSWR, { mutate } from 'swr'
import { fetcher, patchRequest, staticOptions } from '../core/api-client'
import { apiURI } from '../utils/constants'
import { useLocale } from '../hooks/useLocal'
import { createContext, useContextSelector } from 'use-context-selector'
import { useLoader } from './LoaderContext'
import useConfirmOnRouteChanges from '../utils/custom-hooks/useConfirmOnRouteChanges'

import { useTemporary } from './TemporaryContext'
import {
  CreditNameType,
  EarnMessageType,
  EarnTileCustomiseUiType,
  RewardsConfigType,
  RewardsContextType,
  defaultRewardsConfig,
  rewardsPayload
} from '../types/rewards'
import { errorToast, successToast } from '../utils/toasters'

import { useActivityLogs, EntityName } from '../hooks/useActivityLogs'
import { parseRewardsConfigFromBackend } from '../utils/rewards'

export enum ActionType {
  SET_REWARDS_ACTIVE,
  SET_CREDIT_NAME,
  SET_EARN_MESSAGE,
  SET_EARN_TILE_CUSTOMISATION_UI,
  SET_DISCARD_CHANGES,
  SET_COMPLETE_REWARDS_CONFIG
}

type Action =
  | {
      type: ActionType.SET_CREDIT_NAME
      payload: CreditNameType
    }
  | {
      type: ActionType.SET_EARN_MESSAGE
      payload: EarnMessageType
    }
  | {
      type: ActionType.SET_EARN_TILE_CUSTOMISATION_UI
      payload: EarnTileCustomiseUiType
    }
  | {
      type: ActionType.SET_DISCARD_CHANGES
      payload: RewardsConfigType
    }
  | {
      type: ActionType.SET_REWARDS_ACTIVE
      payload: boolean
    }
  | {
      type: ActionType.SET_COMPLETE_REWARDS_CONFIG
      payload: RewardsConfigType
    }

const RewardsContext = createContext<RewardsContextType | undefined>(undefined)

function reducer(state: RewardsConfigType, action: Action): RewardsConfigType {
  switch (action.type) {
    case ActionType.SET_COMPLETE_REWARDS_CONFIG: {
      return action.payload
    }
    case ActionType.SET_CREDIT_NAME: {
      return {
        ...state,
        creditName: {
          ...state?.creditName,
          singularName: action.payload?.singularName,
          pluralName: action.payload?.pluralName
        }
      }
    }
    case ActionType.SET_EARN_MESSAGE: {
      return {
        ...state,
        earnMessage: {
          ...state?.earnMessage,
          enable: action.payload?.enable,
          line_1: action.payload?.line_1,
          line_2: action.payload?.line_2
        }
      }
    }
    case ActionType.SET_EARN_TILE_CUSTOMISATION_UI: {
      return {
        ...state,
        earnTileCustomiseUi: {
          textColor: action.payload?.textColor,
          outlineColor: action.payload?.outlineColor,
          backgroundColor: action.payload?.backgroundColor,
          isCreditNameVisible: action.payload?.isCreditNameVisible,
          outline: action.payload?.outline
        }
      }
    }
    case ActionType.SET_DISCARD_CHANGES: {
      return action.payload
    }
    default: {
      return state
    }
  }
}
const ISSUER_ID = process?.env?.NEXT_PUBLIC_APP_ISSUER_ID
export const RewardsConfigProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()
  const { temporary, setTemporaryData } = useTemporary()
  const {
    getters: { merchant }
  } = useMerchant()
  const {
    data: rewardsResponse,
    error: rewardsResponseError,
    isValidating
  } = useSWR(
    Boolean(merchant?.accountId) && Boolean(ISSUER_ID)
      ? [`/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`, apiURI.REWARDS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const [initialRewardsConfig, setInitialRewardsConfig] = useState<RewardsConfigType>(defaultRewardsConfig)
  const [reducerState, dispatch] = useReducer(reducer, initialRewardsConfig || defaultRewardsConfig)
  useConfirmOnRouteChanges(
    JSON.stringify(initialRewardsConfig) !== JSON.stringify(reducerState),
    () => {
      return new Promise((resolve: any, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          setTemporaryData({ ...temporary, reducerState })
          resolve(true)
        } else reject(false)
      })
    },
    reducerState
  )
  useEffect(() => {
    const creditName: CreditNameType = {
      singularName: (rewardsResponse as any)?.data?.coin_name ?? defaultRewardsConfig?.creditName?.singularName,
      pluralName: (rewardsResponse as any)?.data?.coin_name_plural ?? defaultRewardsConfig?.creditName?.pluralName
    }
    const earnMessage: EarnMessageType = {
      enable: (rewardsResponse as any)?.data?.personalized_wallet_display_message_enabled ?? false,
      line_1:
        (rewardsResponse as any)?.data?.rewards_earned_message?.replace('<rewards_value>', '<Rewards_value>') ??
        defaultRewardsConfig?.earnMessage?.line_1,
      line_2:
        (rewardsResponse as any)?.data?.currency_earned_message?.replace('<currency_value>', '<Rupees_value>') ??
        defaultRewardsConfig?.earnMessage?.line_2
    }
    const earnTileCustomiseUi: EarnTileCustomiseUiType = {
      textColor:
        (rewardsResponse as any)?.data?.earn_tile_customise_ui?.text_color ??
        defaultRewardsConfig?.earnTileCustomiseUi?.textColor,
      outlineColor:
        (rewardsResponse as any)?.data?.earn_tile_customise_ui?.outline_color ??
        defaultRewardsConfig?.earnTileCustomiseUi?.outlineColor,
      backgroundColor:
        (rewardsResponse as any)?.data?.earn_tile_customise_ui?.background_color ??
        defaultRewardsConfig?.earnTileCustomiseUi?.backgroundColor,
      isCreditNameVisible:
        (rewardsResponse as any)?.data?.earn_tile_customise_ui?.is_credit_name_visible ??
        defaultRewardsConfig?.earnTileCustomiseUi?.isCreditNameVisible,
      outline:
        (rewardsResponse as any)?.data?.earn_tile_customise_ui?.outline ??
        defaultRewardsConfig?.earnTileCustomiseUi?.outline
    }

    setInitialRewardsConfig(prevState => ({
      ...prevState,
      creditName: creditName,
      earnMessage: earnMessage,
      earnTileCustomiseUi: earnTileCustomiseUi
    }))
    if (temporary?.reducerState) {
      setCreditName(temporary?.reducerState?.creditName)
      setEarnTileCustomisation(temporary?.reducerState?.earnTileCustomiseUi)
      setTemporaryData({ ...temporary, reducerState: undefined })
      return
    }
    setEarnMessage(earnMessage)
    setCreditName(creditName)
    setEarnTileCustomisation(earnTileCustomiseUi)
  }, [rewardsResponse])

  const setCreditName = useCallback((creditName: CreditNameType) => {
    dispatch({
      type: ActionType.SET_CREDIT_NAME,
      payload: creditName
    })
  }, [])

  const setCompleteRewardsConfig = useCallback((rewardsConfig: RewardsConfigType) => {
    dispatch({
      type: ActionType.SET_COMPLETE_REWARDS_CONFIG,
      payload: rewardsConfig
    })
  }, [])

  const setEarnMessage = useCallback((earnMessage: EarnMessageType) => {
    dispatch({
      type: ActionType.SET_EARN_MESSAGE,
      payload: earnMessage
    })
  }, [])

  const setEarnTileCustomisation = useCallback((earnTileCustomiseUi: EarnTileCustomiseUiType) => {
    dispatch({
      type: ActionType.SET_EARN_TILE_CUSTOMISATION_UI,
      payload: earnTileCustomiseUi
    })
  }, [])

  const setDiscardChanges = useCallback(() => {
    dispatch({
      type: ActionType.SET_DISCARD_CHANGES,
      payload: initialRewardsConfig
    })
  }, [initialRewardsConfig])

  const state = useMemo(
    () => ({
      ...reducerState
    }),
    [reducerState]
  )
  const { sendActivityLog } = useActivityLogs()

  const setSaveChanges = useCallback(
    async (rewardsConfig: RewardsConfigType) => {
      if (
        [
          rewardsConfig?.creditName?.singularName,
          rewardsConfig?.creditName?.pluralName,
          rewardsConfig?.earnMessage?.line_1
        ].includes('')
      ) {
        errorToast(t('please_enter_all_the_fields'))
        return
      }
      const payload = rewardsPayload(rewardsConfig)
      setLoaderProperties({ isLoaderVisible: true, message: 'Updating rewards configuration' })
      try {
        const response: any = await patchRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
          apiURI.REWARDS_ENGINE,
          payload
        )
        const activityLogPayload = {
          entityName: EntityName.REWARDS,
          actionName: 'edited the configuration'
        }
        sendActivityLog(activityLogPayload)
        const parsedRewardsConfig: RewardsConfigType = parseRewardsConfigFromBackend(response?.data?.data)
        setCompleteRewardsConfig(parsedRewardsConfig)
        setInitialRewardsConfig(parsedRewardsConfig)
      } catch (e) {
        console.error(e)
        errorToast('Something went wrong')
      } finally {
        setLoaderProperties({ isLoaderVisible: false })
        successToast('Rules saved successfully.')
      }
    },
    [merchant?.accountId, t, setLoaderProperties, sendActivityLog, setCompleteRewardsConfig]
  )

  const actions = useMemo(
    () => ({
      setCreditName,
      setEarnMessage,
      setEarnTileCustomisation,
      setDiscardChanges,
      setSaveChanges
    }),
    [setCreditName, setEarnMessage, setEarnTileCustomisation, setDiscardChanges, setSaveChanges]
  )

  const value: RewardsContextType = useMemo(
    () => ({
      initialRewardsConfig,
      state,
      actions
    }),
    [actions, state, initialRewardsConfig]
  )
  return <RewardsContext.Provider value={value}>{children}</RewardsContext.Provider>
}

export function useRewardsContext<Selected>(selector: (value: RewardsContextType) => Selected) {
  const { t } = useLocale()
  return useContextSelector(RewardsContext, context => {
    if (context === undefined) {
      throw new Error(t('error_Rewards_context_not_found'))
    }
    return selector(context)
  })
}
